import moment from "moment"
import isNil from "lodash/isNil"
import { I18n } from "utils/i18n-shim"

export const BLANK_PLACEHOLDER = "—"

export const blankToDash = (value) => value || BLANK_PLACEHOLDER

export const resolveLocale = (locale) => (locale === "en" ? "en-GB" : "de-DE")

const format = (value, defaults, { locale, ...options } = {}) =>
  new Intl.NumberFormat(resolveLocale(locale), {
    ...defaults,
    ...options
  }).format(value)

/**
 * @param {number} price
 * @param {Intl.NumberFormatOptions & { locale?: string }} [options]
 * @returns {string} formatted price
 */
export const formatPrice = (price, options = {}) =>
  format(
    price,
    {
      style: "currency",
      currency: "EUR",
      minimumFractionDigits: 0,
      maximumFractionDigits: options.minimumFractionDigits ?? 0
    },
    options
  )

/**
 * @param {number} price
 * @param {Intl.NumberFormatOptions & { locale?: string }} [options]
 * @returns {string} formatted price
 */
export const formatPriceWithCents = (price, options = {}) =>
  format(
    price,
    {
      style: "currency",
      currency: "EUR",
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    },
    options
  )

/**
 * @param {number} value
 * @param {Intl.NumberFormatOptions & { locale?: string }} [options]
 * @returns {string} formatted number
 */
export const formatNumber = (value, options = {}) =>
  format(
    value,
    { style: "decimal", minimumFractionDigits: 0, maximumFractionDigits: 0 },
    options
  )

/**
 * @param {number} [value]
 * @param {Intl.NumberFormatOptions & { locale?: string }} [options]
 * @returns {string} formatted number, or a dash when the value is missing
 */
export const renderNumber = (value, options = {}) =>
  isNil(value) ? BLANK_PLACEHOLDER : formatNumber(value, options)

/**
 * @param {number} [price]
 * @param {Intl.NumberFormatOptions & { locale?: string }} [options]
 * @returns {string} formatted price, or a dash when the value is missing
 */
export const renderPrice = (price, options = {}) =>
  isNil(price) ? BLANK_PLACEHOLDER : formatPrice(price, options)

/**
 * @param {number} [price]
 * @param {Intl.NumberFormatOptions & { locale?: string }} [options]
 * @returns {string} formatted price with cents, or a dash when missing
 */
export const renderPriceWithCents = (price, options = {}) =>
  isNil(price) ? BLANK_PLACEHOLDER : formatPriceWithCents(price, options)

/**
 * @param {number} rate a fraction, e.g. 0.125 for 12.5 %
 * @param {Intl.NumberFormatOptions & { locale?: string }} [options]
 * @returns {string|null} formatted percentage
 */
export const formatRate = (rate, options = {}) => {
  if (rate === null || rate === undefined) return null

  return `${format(
    rate * 100,
    { minimumFractionDigits: 1, maximumFractionDigits: 1 },
    options
  )} %`
}

export const formatApiDate = (date) =>
  date ? moment(date).format("YYYY-MM-DD") : null

const defaultFormat = "DD.MM.YYYY HH:mm"
export const formatDateTime = (date, format = defaultFormat) =>
  moment(date).format(format)

export const API_DATE_FORMAT = "YYYY-MM-DD"
export const toApiDate = (date) =>
  date ? moment(date).format(API_DATE_FORMAT) : date

export const localizedDate = (date) =>
  date && I18n.l(date, { dateFormat: "time.formats.short" })

export const localizedDateTime = (date, { seconds = true } = {}) =>
  date &&
  I18n.l(date, {
    dateFormat: seconds
      ? "time.formats.date-time"
      : "time.formats.date-time-short"
  })

export const localizedTime = (date) =>
  date && I18n.l(date, { dateFormat: "time.formats.time" })

export const formatDuration = (duration) => {
  if (duration !== null && duration !== undefined) {
    const seconds = Math.floor(duration % 60)
    const minutes = Math.floor((duration % 3600) / 60)
    const hours = Math.floor(duration / 3600)

    return [
      `${hours < 10 ? "0" : ""}${hours}`,
      `${minutes < 10 ? "0" : ""}${minutes}`,
      `${seconds < 10 ? "0" : ""}${seconds}`
    ].join(":")
  }

  return null
}
